/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDeviceType } from "../../../shared/hooks/useDeviceType";
import { isScheduledLiveScreenMediaType } from "../../../shared/utils/media";
import {
  usePlayerController,
  usePlayerTimer,
  UserPlayerErrorMessage,
} from "../../BasePlayer";
import {
  useKeyboardControls,
  usePlayerContinueWatching,
  usePlayerCurrentMedia,
  usePlayerErrorHandler,
  usePlayerNextEpisode,
} from "../../Controller";
import { useCustomPlayerContext } from "../../CustomPlayerContext";
import { LiveScreen } from "../../LiveScreen";

import { AdBreakOverlay } from "./AdBreakOverlay";
import { ContinueWatchingOverlay } from "./ContinueWatchingOverlay";
import { ErrorOverlay } from "./ErrorOverlay";
import { InitPlaybackOverlay } from "./InitPlaybackOverlay";
import { NextEpisodeOverlay } from "./NextEpisodeOverlay";
import { PlayingOverlay } from "./PlayingOverlay";
import { useScreenOrientationLocker } from "./useScreenOrientationLocker";

type OverlayState =
  | {
      type:
        | "init-playback"
        | "playing"
        | "ad-break"
        | "ad-loading"
        | "continue-watching"
        | "ended"
        | "live-ended"
        | "next-episode-dismissed";
    }
  | {
      type: "error";
      error: UserPlayerErrorMessage;
    }
  | {
      type: "next-episode";
      timer: number;
    };

type OverlayType = OverlayState["type"];

const disabledOrientationLock: OverlayType[] = [
  "init-playback",
  "continue-watching",
  "error",
  "live-ended",
];

const LIVE_ENDED_SCREEN_DELAY_MS = 3000;

export const useOverlayContainerSelector = (): JSX.Element => {
  const [overlay, setOverlay] = useState<OverlayState>({
    type: "init-playback",
  });

  const { iPhoneDevice } = useDeviceType();
  const { player } = usePlayerController();
  const { media, playInfo } = usePlayerCurrentMedia();
  const { showOverlay: showContinueWatching } = usePlayerContinueWatching();
  const { liveScreens } = useCustomPlayerContext();
  const { timer } = usePlayerTimer();
  const timerRef = useRef(timer);
  const liveEndedOverlayTimeoutIdRef = useRef<number>();

  useKeyboardControls({
    enabled: overlay.type === "playing" || overlay.type === "ended",
  });

  useScreenOrientationLocker(!disabledOrientationLock.includes(overlay.type));

  const onError = useCallback(
    (error: UserPlayerErrorMessage) => setOverlay({ type: "error", error }),
    [],
  );

  const onShowNextEpisode = useCallback((timer: number) => {
    setOverlay({ type: "next-episode", timer });
  }, []);

  usePlayerErrorHandler(onError);
  usePlayerNextEpisode(onShowNextEpisode);

  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  const clearLiveEndedOverlayTimeout = useCallback(() => {
    if (liveEndedOverlayTimeoutIdRef.current) {
      window.clearTimeout(liveEndedOverlayTimeoutIdRef.current);
      liveEndedOverlayTimeoutIdRef.current = undefined;
    }
  }, []);

  const isAtLivePlaybackEnd = useCallback(() => {
    const latestTimer = timerRef.current;
    const isAtCurrentLiveEnd =
      latestTimer.atLiveEdge ||
      (latestTimer.isLive &&
        Math.abs(player.liveSeekableEnd() - player.currentTime()) <= 2);
    const isAtDvrEnd =
      latestTimer.duration !== Infinity &&
      latestTimer.duration > 0 &&
      latestTimer.duration - latestTimer.currentTime <= 2;

    return isAtCurrentLiveEnd || isAtDvrEnd;
  }, [player]);

  useEffect(() => {
    showContinueWatching && setOverlay({ type: "continue-watching" });
  }, [showContinueWatching]);

  useEffect(() => {
    const unsubscribe = player.onEvent("playing", () => {
      clearLiveEndedOverlayTimeout();

      // on iPhone, we always want to play video in full screen mode
      iPhoneDevice && player.requestNativeFullScreen();

      setOverlay({ type: "playing" });
    });

    return () => unsubscribe();
  }, [clearLiveEndedOverlayTimeout, iPhoneDevice, player]);

  useEffect(() => {
    const isLiveMedia =
      isScheduledLiveScreenMediaType(media.MediaTypeCode) ||
      isScheduledLiveScreenMediaType(playInfo.MediaTypeCode);

    if (!liveScreens || !isLiveMedia) {
      return;
    }

    const unsubscribe = player.onEvent("ended", () => {
      if (!isAtLivePlaybackEnd()) {
        return;
      }

      clearLiveEndedOverlayTimeout();
      liveEndedOverlayTimeoutIdRef.current = window.setTimeout(() => {
        liveEndedOverlayTimeoutIdRef.current = undefined;

        if (isAtLivePlaybackEnd()) {
          setOverlay({ type: "live-ended" });
        }
      }, LIVE_ENDED_SCREEN_DELAY_MS);
    });

    return () => {
      clearLiveEndedOverlayTimeout();
      unsubscribe();
    };
  }, [
    clearLiveEndedOverlayTimeout,
    isAtLivePlaybackEnd,
    liveScreens,
    media.MediaTypeCode,
    playInfo.MediaTypeCode,
    player,
  ]);

  useEffect(() => {
    const unsubscribeAdPlaying = player.onEvent(["adstarted"], () => {
      setOverlay({ type: "ad-break" });
    });

    const unsubscribeAdEnded = player.onEvent(["adend", "adlinearmodend"], () =>
      setOverlay({ type: "playing" }),
    );

    return () => {
      unsubscribeAdPlaying();
      unsubscribeAdEnded();
    };
  }, [player]);

  const handleDismissNextEpisode = useCallback(() => {
    setOverlay({ type: "next-episode-dismissed" });
  }, [setOverlay]);

  switch (overlay.type) {
    case "init-playback":
      return <InitPlaybackOverlay />;
    case "continue-watching":
      return <ContinueWatchingOverlay />;
    case "playing":
    case "ended":
      return <PlayingOverlay />;
    case "live-ended":
      return <LiveScreen type="ended" config={liveScreens!} />;
    case "ad-break":
      return <AdBreakOverlay />;
    case "next-episode":
      return (
        <NextEpisodeOverlay
          timer={overlay.timer}
          handleDismiss={handleDismissNextEpisode}
        />
      );
    case "error":
      return <ErrorOverlay error={overlay.error} />;
    case "next-episode-dismissed":
      return <PlayingOverlay />;
    default:
      throw new Error("Unsupported player overlay");
  }
};
